import React from 'react';

import { ScrollView } from 'native-base';

import { IPicker, IPickerAspect } from '@rbi-ctg/menu';
import { InnerLayout, LayoutContainer } from 'components/layout/styled';
import { ProductCta } from 'components/product-detail-v2/product-cta';
import { ProductDetailHeader } from 'components/product-detail-v2/product-detail-flow/product-detail-header';
import { FIRST_INDEX } from 'components/product-detail-v2/product-detail-flow/product-picker-flow/constants';
import {
  transformComboSlotTypeToEventAttr,
  transformProductWizardStepToEventAttr,
} from 'components/product-detail-v2/utils';
import { MenuObjectTypes } from 'enums/menu';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { ProductWizardStep, UserSelectionPickerAspect } from 'state/product-wizard-v2/types';

import { ComboSlotType } from '../ComboSlotType';
import { ProductCustomizationPickerAspects } from '../product-customization-picker-aspects';

interface IProductCustomizationPickerProps {
  pickerData: IPicker;
  pickerSlotNumberToCustomize: string;
  userSelectedPickerAspects: UserSelectionPickerAspect | undefined;
  filteredPickerAspects: IPickerAspect[];
  getPreviousPickerSelectionIdentifiers: (startPoint: number, endPoint: number) => string[];
  selectedPickerAspects: UserSelectionPickerAspect;
  setSelectedPickerAspects: React.Dispatch<React.SetStateAction<UserSelectionPickerAspect>>;
  onPressBackNavigation: () => void;
  backNavigationLabel?: string;
}

export const ProductCustomizationPicker: React.FC<IProductCustomizationPickerProps> = ({
  pickerData,
  pickerSlotNumberToCustomize,
  userSelectedPickerAspects,
  filteredPickerAspects,
  getPreviousPickerSelectionIdentifiers,
  selectedPickerAspects,
  setSelectedPickerAspects,
  onPressBackNavigation,
  backNavigationLabel = '',
}) => {
  const isMobile = useIsMobileBreakpoint();
  const { updateProductWizardFlowSteps } = useProductWizardContext();

  const currentPickerAspect = filteredPickerAspects.find(
    aspect => aspect._id === pickerSlotNumberToCustomize
  );

  React.useEffect(() => {
    const isFirstPickerAspect = filteredPickerAspects.indexOf(currentPickerAspect!) === FIRST_INDEX;

    updateProductWizardFlowSteps({
      id: currentPickerAspect?._id ?? '',
      type: MenuObjectTypes.PICKER_ASPECT,
      currentStep: true,
      isFirstStep: isFirstPickerAspect,
    });
  }, [currentPickerAspect, filteredPickerAspects, updateProductWizardFlowSteps]);

  return (
    <>
      <ProductDetailHeader
        currentStep={ProductWizardStep.ProductCustomization}
        comboSlotType={ComboSlotType.PICKER_ASPECT}
        onPress={onPressBackNavigation}
        backNavigationLabel={backNavigationLabel}
        eventAttributes={{
          name: transformProductWizardStepToEventAttr(ProductWizardStep.ProductCustomization),
          comboSlotType: transformComboSlotTypeToEventAttr(ComboSlotType.PICKER_ASPECT),
        }}
      />

      <ScrollView showsVerticalScrollIndicator>
        <LayoutContainer isFullContainer={isMobile}>
          <InnerLayout>
            <ProductCustomizationPickerAspects
              pickerData={pickerData}
              pickerSlotNumberToCustomize={pickerSlotNumberToCustomize}
              userSelectedPickerAspects={userSelectedPickerAspects}
              filteredPickerAspects={filteredPickerAspects}
              getPreviousPickerSelectionIdentifiers={getPreviousPickerSelectionIdentifiers}
              selectedPickerAspects={selectedPickerAspects}
              setSelectedPickerAspects={setSelectedPickerAspects}
            />
          </InnerLayout>
        </LayoutContainer>
      </ScrollView>
      <ProductCta currentStep={ProductWizardStep.ProductCustomization} menuObject={pickerData} />
    </>
  );
};
